/**
 * Solex Document Editor Custom Hook
 *
 * Encapsulates all Solex window management logic with feature flag integration.
 * Provides automatic opening, manual fallback, and popup blocker detection.
 *
 * Replaces ~120 lines of inline logic in OrderButton.tsx (EN-3255).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../state/store';
import { setLoadingState } from '../state/appSlice';
import { openSolexEditor } from '../services/solexEditorService';
import { openPlaceholderWindow, cleanupWindow } from '../utils/solexWindowUtils';
import { useFeatureFlag, FeatureFlags } from '../featureFlags';

interface UseSolexEditorReturn {
  solexLink: string | null;
  isPopupBlocked: boolean;
  isOpeningEditor: boolean;
  error: Error | null;
  isEnabled: boolean;
  openAutomatically: (link: string) => Promise<boolean>;
  openManually: (link?: string) => Promise<boolean>;
  reset: () => void;
}

/**
 * Custom hook for managing Solex Document Editor integration
 *
 * @param alertHandler Optional handler for toast notifications (message, severity)
 * @returns Object with state and methods for Solex editor integration
 */
export const useSolexEditor = (
  alertHandler?: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void
): UseSolexEditorReturn => {
  const dispatch = useDispatch();

  // Three-tier gating system for Solex feature (EN-3255)
  // Gate 1: LaunchDarkly feature flag (environment/rollout control)
  const featureFlagEnabled = useFeatureFlag(FeatureFlags.UI_SOLEX_EDITOR_ENABLED);

  // Gate 2: Backend configuration flag (client-level control)
  const configFlagEnabled = useSelector(
    (state: RootState) => state.configPlugInData.data?.Configuration?.EnableSolexDocEditor ?? false
  );

  // Combined gate: BOTH flags must be true
  const isEnabled = featureFlagEnabled && configFlagEnabled;

  // State
  const [solexLink, setSolexLink] = useState<string | null>(null);
  const [isPopupBlocked, setIsPopupBlocked] = useState(false);
  const [isOpeningEditor, setIsOpeningEditor] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Refs for cleanup
  const windowRef = useRef<Window | null>(null);

  // Refs for flag values (used in callbacks for logging without triggering re-creation)
  const featureFlagRef = useRef(featureFlagEnabled);
  const configFlagRef = useRef(configFlagEnabled);

  // Keep flag refs synchronized with current values
  useEffect(() => {
    featureFlagRef.current = featureFlagEnabled;
    configFlagRef.current = configFlagEnabled;
  }, [featureFlagEnabled, configFlagEnabled]);

  /**
   * Core opening logic shared between automatic and manual flows
   *
   * @param link Solex editor link
   * @param preOpenedWindow Optional pre-opened window to use
   * @returns true if successfully opened, false otherwise
   */
  const openEditorWithWindow = useCallback(
    async (link: string, preOpenedWindow?: Window | null): Promise<boolean> => {
      // Early return if combined gating disables the feature
      if (!isEnabled) {
        console.log(
          `[Solex] Solex Document Editor disabled (featureFlagEnabled=${featureFlagRef.current}, configFlagEnabled=${configFlagRef.current})`
        );
        return false;
      }

      try {
        setIsOpeningEditor(true);
        setError(null);

        // Use pre-opened window or open new one
        const targetWindow = preOpenedWindow || openPlaceholderWindow();
        windowRef.current = targetWindow;

        if (!targetWindow) {
          console.error('[Solex] Window opening failed - popup blocked');
          setIsPopupBlocked(true);
          setSolexLink(link);
          setIsOpeningEditor(false);

          // Toast notification for popup blocked
          alertHandler?.(
            'Popup blocker detected. Please use the manual button to open the Solex editor.',
            'warning'
          );

          return false;
        }

        console.log('[Solex] Window opened successfully');

        // Show loading state
        dispatch(
          setLoadingState({
            loading: true,
            loadingMessage: 'Opening Solex Document Editor...',
          })
        );

        // Call service to exchange token and submit form
        const success = await openSolexEditor(link, targetWindow);

        dispatch(setLoadingState({ loading: false, loadingMessage: '' }));

        if (success) {
          console.log('[Solex] Document Editor opened successfully');

          // Toast notification for success
          alertHandler?.('Solex Document Editor opened successfully', 'success');

          setSolexLink(null);
          setIsPopupBlocked(false);
          windowRef.current = null;
          return true;
        } else {
          console.error('[Solex] Failed to open Document Editor');
          cleanupWindow(targetWindow);
          setError(new Error('Failed to open Solex Document Editor'));

          // Toast notification for failure
          alertHandler?.('Failed to open Solex Document Editor. Please try again.', 'error');

          return false;
        }
      } catch (err) {
        console.error('[Solex] Error opening Document Editor:', err);
        dispatch(setLoadingState({ loading: false, loadingMessage: '' }));
        cleanupWindow(windowRef.current);
        setError(err as Error);
        setIsOpeningEditor(false);

        // Toast notification for error
        alertHandler?.(
          'Error opening Solex Document Editor: ' + (err as Error).message,
          'error'
        );

        return false;
      } finally {
        setIsOpeningEditor(false);
      }
    },
    [dispatch, isEnabled, alertHandler]
  );

  /**
   * Automatically open Solex editor (called after order submission)
   *
   * Opens a placeholder window immediately to prevent popup blockers,
   * then loads the Solex editor URL.
   *
   * @param link Solex editor link from API response
   * @returns true if successfully opened, false if blocked
   */
  const openAutomatically = useCallback(
    async (link: string): Promise<boolean> => {
      if (!isEnabled) {
        console.log(
        `[Solex] Automatic open skipped - feature disabled (featureFlagEnabled=${featureFlagRef.current}, configFlagEnabled=${configFlagRef.current})`
      );
        return false;
      }

      console.log('[Solex] Attempting automatic open for link:', link);
      setSolexLink(link);

      const placeholderWindow = openPlaceholderWindow();

      if (!placeholderWindow) {
        console.error('[Solex] Automatic window opening blocked');
        setIsPopupBlocked(true);
        return false;
      }

      return await openEditorWithWindow(link, placeholderWindow);
    },
    [openEditorWithWindow, isEnabled]
  );

  /**
   * Manually open Solex editor (called when user clicks button)
   *
   * This is a fresh opening attempt triggered by direct user click.
   * Uses form submission instead of window.open() to avoid popup blockers.
   *
   * @param link Optional link to open (uses stored link if not provided)
   * @returns true if successfully opened, false otherwise
   */
  const openManually = useCallback(
    async (link?: string): Promise<boolean> => {
      if (!isEnabled) {
        console.log(
        `[Solex] Manual open skipped - feature disabled (featureFlagEnabled=${featureFlagRef.current}, configFlagEnabled=${configFlagRef.current})`
      );
        return false;
      }

      // Use provided link or stored link
      const targetLink = link || solexLink;

      if (!targetLink) {
        console.error('[Solex] No link available for manual opening');
        return false;
      }

      setIsOpeningEditor(true);
      console.log('[Solex] Manual button clicked - form submission approach');

      try {
        // Show loading state
        dispatch(
          setLoadingState({
            loading: true,
            loadingMessage: 'Opening Solex Document Editor...',
          })
        );

        // Call service directly - it will use form submission (no window.open needed!)
        // Form submission with target="_blank" is more reliable than window.open()
        const success = await openSolexEditor(targetLink);

        dispatch(setLoadingState({ loading: false, loadingMessage: '' }));

        if (success) {
          console.log('[Solex] Document Editor opened successfully via form');

          // Toast notification for success
          alertHandler?.('Solex Document Editor opened successfully', 'success');

          setSolexLink(null);
          setIsPopupBlocked(false);
          return true;
        } else {
          console.error('[Solex] Failed to open Document Editor');

          // Toast notification for failure
          alertHandler?.('Failed to open Solex Document Editor. Please try again.', 'error');

          return false;
        }
      } catch (err) {
        console.error('[Solex] Error opening Document Editor:', err);
        dispatch(setLoadingState({ loading: false, loadingMessage: '' }));

        // Toast notification for error
        alertHandler?.(
          'Error opening Solex Document Editor: ' + (err as Error).message,
          'error'
        );

        return false;
      } finally {
        setIsOpeningEditor(false);
      }
    },
    [solexLink, isEnabled, alertHandler, dispatch]
  );

  /**
   * Reset all state (useful when unmounting or starting new order)
   */
  const reset = useCallback(() => {
    setSolexLink(null);
    setIsPopupBlocked(false);
    setIsOpeningEditor(false);
    setError(null);
    cleanupWindow(windowRef.current);
    windowRef.current = null;
  }, []);

  return {
    solexLink,
    isPopupBlocked,
    isOpeningEditor,
    error,
    isEnabled,
    openAutomatically,
    openManually,
    reset,
  };
};
